import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import dayjs from 'dayjs';
import { CONSENT_STORAGE_KEY, VENDOR_LIST_VERSION, type ConsentRecord } from './vendors';
import { initSentry } from './sentry';
import { initAnalytics } from './analytics';
import { loadHeadwayWidget } from './headway';

interface ConsentContextValue {
  accepted: boolean;
  accept: () => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

function isConsentValid(record: ConsentRecord): boolean {
  return (
    record.version === VENDOR_LIST_VERSION &&
    dayjs().diff(dayjs(record.acceptedAt), 'month') < 12
  );
}

function readStoredConsent(): boolean {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return false;
    const record = JSON.parse(raw) as ConsentRecord;
    return isConsentValid(record);
  } catch {
    return false;
  }
}

function activateVendors(): void {
  initSentry();
  initAnalytics();
  loadHeadwayWidget();
}

interface ConsentProviderProps {
  children: ReactNode;
}

export function ConsentProvider({ children }: ConsentProviderProps) {
  const [accepted, setAccepted] = useState<boolean>(() => readStoredConsent());

  useEffect(() => {
    if (accepted) {
      activateVendors();
    }
  }, []);

  function accept(): void {
    const record: ConsentRecord = {
      version: VENDOR_LIST_VERSION,
      acceptedAt: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    setAccepted(true);
    activateVendors();
  }

  return (
    <ConsentContext.Provider value={{ accepted, accept }}>
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return ctx;
}
